import React, { useEffect, useState } from 'react'
import {
  BodyText,
  Button,
  HeadingText,
  SegmentedController,
  SegmentedControllerPanel,
  Tab
} from '@paypalcorp/pp-react'
import { get } from 'lodash'
import { CouponTermsType, OfferCardType } from '../../types/ShoppingPageType'
import useBreakpoint from '../../../../lib/useBreakpoint'
import ReadMoreText from '../ReadMoreText'
import OfferCard from './OfferCard/OfferCard'
import { sendFptiClick, sendFptiImpression } from '../../../../lib/fpti-pa'
import {
  SHOPPING_STORE_TAB_PRESSED,
  SHOPPING_STORE_ITEM_SHOWN,
  SHOPPING_STORE_EXPAND_PRESSED
} from '../../../../lib/client-constants'
import { StoreProfileFPTI } from '../../../store-profile/StoreProfilePage'
import useWorldReady from 'hooks/useWorldReady'

type TabId = 'allOffers-tab' | 'deals-tab' | 'codes-tab'
type PanelId = 'allOffers-panel' | 'deals-panel' | 'codes-panel'

interface ShowMoreButtonProps {
  label: string
  isVisible?: boolean
  isMobile: boolean
  onClick: () => void
}

interface Tab {
  label: string
  id: TabId
  panelId: PanelId
}

interface Props {
  isVisible?: boolean
  initialDisplayCards?: number
  descriptionDesktopInitialTextLength?: number
  descriptionMobileInitialTextLength?: number
  descriptionSentenceTruncate?: boolean
  couponTerms: CouponTermsType
  deals: OfferCardType[]
  codes: OfferCardType[]
  landingUrl?: string
  fptiData: StoreProfileFPTI | null
}

const INITIAL_DISPLAY_CARDS = 5
const DESKTOP_TEXT_LENGTH = 150
const MOBILE_TEXT_LENGTH = 150

const ShowMoreButton = ({
  label,
  isVisible = true,
  isMobile,
  onClick
}: ShowMoreButtonProps) => {
  if (!isVisible) {
    return null
  }

  return (
    <div className="offer-section__button-wrapper">
      <Button
        theme="v2"
        secondary
        size={isMobile ? 'sm' : 'lg'}
        className="offer-section__show-more-deals-button"
        onClick={onClick}
      >
        {label}
      </Button>
    </div>
  )
}

const OfferSection = ({
  isVisible = true,
  initialDisplayCards = INITIAL_DISPLAY_CARDS,
  descriptionDesktopInitialTextLength = DESKTOP_TEXT_LENGTH,
  descriptionMobileInitialTextLength = MOBILE_TEXT_LENGTH,
  descriptionSentenceTruncate = true,
  couponTerms,
  deals,
  codes,
  landingUrl,
  fptiData
}: Props): JSX.Element | null => {
  const { formatMessage } = useWorldReady()

  const [selectedTabIdx, setSelectedTabIdx] = useState<0 | 1 | 2>(0)
  const [limitDisplayCards, setLimitDisplayCards] = useState(true)
  const [showMoreClicked, setShowMoreClicked] = useState(false)

  const { isMobile } = useBreakpoint()

  const allOffers = deals
    .concat(codes)
    .sort((offerA, offerB) => offerA.rank - offerB.rank)

  const couponTermsTitle = get(couponTerms, 'title')
  const couponTermsContent = get(couponTerms, 'content')
  const showMoreButtonLabel = formatMessage(
    'main.offerSection.showMoreButtonLabel'
  )
  const readMoreLabel = formatMessage(
    'main.offerSection.readMoreText.readMoreLabel'
  )
  const showLessLabel = formatMessage(
    'main.offerSection.readMoreText.showLessLabel'
  )

  const allOffersTabLabel: string = formatMessage(
    'main.offerSection.tabs.allLabel'
  )
  const dealsTabLabel: string = formatMessage(
    'main.offerSection.tabs.dealsLabel'
  )
  const codesTabLabel: string = formatMessage(
    'main.offerSection.tabs.codesLabel'
  )

  const TABS: Tab[] = [
    {
      label: allOffersTabLabel,
      id: 'allOffers-tab',
      panelId: 'allOffers-panel'
    },
    {
      label: dealsTabLabel,
      id: 'deals-tab',
      panelId: 'deals-panel'
    },
    {
      label: codesTabLabel,
      id: 'codes-tab',
      panelId: 'codes-panel'
    }
  ]

  const handleSegmentedControllerClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tabIdx: number
  ) => {
    event.preventDefault()
    const newIndex = !tabIdx ? 0 : (Number(tabIdx) as 1 | 2)
    if (newIndex === 0) {
      setLimitDisplayCards(
        allOffers.length > initialDisplayCards ? true : false
      )
    }
    if (newIndex === 1) {
      setLimitDisplayCards(deals.length > initialDisplayCards ? true : false)
    }
    if (newIndex === 2) {
      setLimitDisplayCards(codes.length > initialDisplayCards ? true : false)
    }
    setSelectedTabIdx(newIndex)
    sendFptiClick({
      event_name: SHOPPING_STORE_TAB_PRESSED,
      link_name: TABS[newIndex].label.toLowerCase(),
      ...fptiData
    })
  }

  const handleShowMoreDealsClick = () => {
    setLimitDisplayCards(false)
    setShowMoreClicked(true)
    sendFptiClick({
      event_name: SHOPPING_STORE_EXPAND_PRESSED,
      tab: TABS[selectedTabIdx].label.toLowerCase(),
      ...fptiData
    })
  }

  const calculateLimit = (offerArray: Array<OfferCardType>) => {
    return limitDisplayCards &&
      !showMoreClicked &&
      offerArray.length > initialDisplayCards
      ? initialDisplayCards
      : offerArray.length
  }

  const getOfferList = (offerList: OfferCardType[]) => {
    const offerListLimit = calculateLimit(offerList)
    const resultOfferList = offerList
      .slice(0, offerListLimit)
      .map((offer, index) => {
        return (
          <li key={index} className="offer-section__offer-list-item">
            <OfferCard
              offerData={offer}
              landingUrl={landingUrl}
              fptiData={fptiData}
            />
          </li>
        )
      })
    return resultOfferList
  }

  const allOffersList = getOfferList(allOffers)
  const dealsList = getOfferList(deals)
  const codesList = getOfferList(codes)

  useEffect(() => {
    if (!allOffers.length) {
      return
    }
    setLimitDisplayCards(
      deals.length + codes.length > initialDisplayCards ? true : false
    )
  }, [deals, codes, initialDisplayCards, allOffers.length])

  useEffect(() => {
    if (!allOffers.length) {
      return
    }
    const tabOffers = { 0: allOffers, 1: deals, 2: codes }
    const offersSelected = tabOffers[selectedTabIdx]
    const displayOffers = offersSelected.slice(
      0,
      calculateLimit(offersSelected)
    )

    displayOffers.forEach(offer => {
      sendFptiImpression({
        event_name: SHOPPING_STORE_ITEM_SHOWN,
        banner_type: offer.honeyOfferType.toLocaleLowerCase(),
        offer_program_id: offer.honeyOfferId,
        rank: offer.rank,
        ...fptiData
      })
    })

    // To trigger this FPTI event only once a time by item and when tab or showMoreClicked is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIdx, allOffers.length, showMoreClicked])

  if (!isVisible) {
    return null
  }

  return (
    <div className="offer-section">
      <HeadingText size="sm" className="offer-section__heading-text">
        {couponTermsTitle}
      </HeadingText>
      <ReadMoreText
        fptiEvent={() => {}}
        textComponent={BodyText}
        entireText={couponTermsContent}
        readMoreText={readMoreLabel}
        showLessText={showLessLabel}
        maxLength={
          isMobile
            ? descriptionMobileInitialTextLength
            : descriptionDesktopInitialTextLength
        }
        sentenceTruncate={descriptionSentenceTruncate}
        theme="v2"
        className="offer-section"
        buttonClassName="offer-section"
      />
      <SegmentedController
        className="offer-section__segmented-controller"
        name="DealsAndCodes"
        selectedTabIdx={selectedTabIdx}
        tabs={TABS}
        onClick={handleSegmentedControllerClick}
      >
        <SegmentedControllerPanel
          id="allOffers-panel"
          tabId="allOffers-tab"
          className="offer-section__segmented-controller-panel"
        >
          <ul
            className="offer-section__offer-list"
            data-cy="offer-section__offer-list"
          >
            {allOffersList}
          </ul>
        </SegmentedControllerPanel>
        <SegmentedControllerPanel
          id="deals-panel"
          tabId="deals-tab"
          className="offer-section__segmented-controller-panel"
        >
          <ul
            className="offer-section__offer-list"
            data-cy="offer-section__offer-list"
          >
            {dealsList}
          </ul>
        </SegmentedControllerPanel>
        <SegmentedControllerPanel
          id="codes-panel"
          tabId="codes-tab"
          className="offer-section__segmented-controller-panel"
        >
          <ul
            className="offer-section__offer-list"
            data-cy="offer-section__offer-list"
          >
            {codesList}
          </ul>
        </SegmentedControllerPanel>
      </SegmentedController>
      <ShowMoreButton
        label={showMoreButtonLabel}
        isVisible={limitDisplayCards && !showMoreClicked}
        onClick={handleShowMoreDealsClick}
        isMobile={isMobile}
      />
    </div>
  )
}

export default OfferSection
